import { BaseError, ContractFunctionRevertedError, formatEther } from 'viem';

function extractCustomErrorName(text = '') {
  const patterns = [
    /reverted with custom error ['"]([A-Za-z0-9_]+)\(/i,
    /custom error ['"]([A-Za-z0-9_]+)\(/i,
    /Contract reverted(?::)?\s*([A-Za-z0-9_]+)/i,
    /errorName["':\s]+([A-Za-z0-9_]+)/i,
  ];

  const match = patterns.reduce((found, pattern) => {
    if (found) return found;
    return String(text).match(pattern);
  }, null);

  if (match?.[1]) return match[1];

  return null;
}

function mapCustomError(errorName, args = []) {
  switch (errorName) {
    // Your bridge custom errors
    case 'InvalidAddress':
      return 'Invalid address passed to the contract.';
    case 'InsufficientAmount':
      return 'Invalid amount or insufficient contract balance for this operation.';
    case 'AlreadyUsedIntent':
      return 'This swap intent has already been used.';

    // Your older/custom app-level error if still emitted somewhere
    case 'InsufficientUserBalance':
      return 'Insufficient balance for the transaction.';

    // OpenZeppelin / ERC-20 custom errors
    case 'ERC20InsufficientBalance': {
      const [, balance, needed] = args || [];
      if (balance !== undefined && needed !== undefined) {
        return `Insufficient token balance. Wallet balance is lower than the amount required for this deposit.`;
      }
      return 'Insufficient token balance for this transaction.';
    }

    case 'ERC20InsufficientAllowance':
      return 'Token approval is too low for this transaction. Please approve the token and try again.';

    case 'ERC20InvalidSender':
      return 'Invalid token sender address.';
    case 'ERC20InvalidReceiver':
      return 'Invalid token receiver address.';
    case 'ERC20InvalidApprover':
      return 'Invalid token approver address.';
    case 'ERC20InvalidSpender':
      return 'Invalid token spender address.';

    // OpenZeppelin Pausable / Ownable custom errors
    case 'EnforcedPause':
      return 'Contract is paused right now.';
    case 'ExpectedPause':
      return 'This action requires the contract to be paused.';
    case 'OwnableUnauthorizedAccount':
      return 'Unauthorized account for this action.';
    case 'OwnableInvalidOwner':
      return 'Invalid owner address.';

    // Generic Solidity errors
    case 'Error':
      if (args?.[0]) return `Transaction failed: ${args[0]}`;
      return 'Transaction failed.';
    case 'Panic':
      return 'Smart contract panic error.';

    default:
      return null;
  }
}

function parseInsufficientFunds(text = '') {
  const match = String(text).match(/have\s+(\d+)\s+want\s+(\d+)/i);
  if (!match) return null;

  const have = BigInt(match[1]);
  const want = BigInt(match[2]);

  return { have, want };
}

function isInsufficientGasFundsText(text = '') {
  return /insufficient funds for gas|funds for gas \* price \+ value|base fee/i.test(String(text));
}

export function handleContractError(err) {
  if (!err) return 'An unknown error occurred. Please try again.';

  const name = String(err?.name || '');
  const msg = String(err?.message || '');
  const shortMessage = String(err?.shortMessage || '');
  const details = String(err?.details || '');
  const fullText = [name, shortMessage, msg, details].filter(Boolean).join(' | ');

  if (isInsufficientGasFundsText(fullText)) {
    const parsed = parseInsufficientFunds(fullText);

    if (parsed) {
      return `Not enough native token for gas. You have ${formatEther(parsed.have)} ETH but need about ${formatEther(parsed.want)} ETH.`;
    }

    return 'Not enough native token to pay gas fees.';
  }

  // 1) User rejected in wallet
  if (
    name === 'UserRejectedRequestError' ||
    msg.includes('User rejected') ||
    shortMessage.includes('User rejected') ||
    details.includes('User denied transaction signature')
  ) {
    return 'User rejected the transaction.';
  }

  // 2) Best case: viem decoded revert data cleanly
  if (err instanceof BaseError) {
    const revert = err.walk((e) => e instanceof ContractFunctionRevertedError);

    if (revert) {
      const errorName = revert.data?.errorName;
      const args = revert.data?.args || [];

      const mapped = mapCustomError(errorName, args);
      if (mapped) return mapped;

      if (errorName) return `Contract reverted: ${errorName}.`;
    }

    // 3) If viem did not decode it cleanly, parse wrapped RPC text
    const customErrorFromText = extractCustomErrorName(fullText);
    if (customErrorFromText) {
      const mapped = mapCustomError(customErrorFromText);
      if (mapped) return mapped;
      return `Contract reverted: ${customErrorFromText}.`;
    }

    if (err?.shortMessage) return err.shortMessage;
    return 'Transaction simulation failed.';
  }

  // 4) Fallback parsing for non-BaseError wrapped provider errors
  const customErrorFromText = extractCustomErrorName(fullText);
  if (customErrorFromText) {
    const mapped = mapCustomError(customErrorFromText);
    if (mapped) return mapped;
    return `Contract reverted: ${customErrorFromText}.`;
  }

  // 5) Common provider/RPC errors
  if (fullText.match(/insufficient funds|funds for gas|base fee/i)) {
    return 'Insufficient funds for gas/fees.';
  }

  if (fullText.match(/replacement fee too low|underpriced|maxfeepergas|priority fee/i)) {
    return 'Gas price too low. Try again with higher fees.';
  }

  if (fullText.match(/nonce too low/i)) {
    return 'Nonce too low. Wait for pending transactions or reset the nonce.';
  }

  if (fullText.match(/execution reverted/i)) {
    return 'Execution reverted by the EVM.';
  }

  // 6) Plain JS Error
  if (err instanceof Error && err.message) {
    return err.message;
  }

  if (typeof err === 'string') return err;

  return 'Transaction could not be processed. Please try again.';
}
